// Игровой мир (§2.18): GET экрана мира и действия. Всё — с Bearer (гость — тоже аккаунт; сессия заводится лениво).
// Пояс суток (задания дня) — заголовок X-Timezone (с 10.10 CORS сервера его пропускает).
// Каждое действие отвечает ЦЕЛЫМ экраном мира (+ outcome, reward) — кладём его в кэш как есть, второго запроса нет.
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ApiError, apiRequest } from './client';
import { deviceTimeZone } from './gameQueries';
import type { WorldGems, GameWorld, WorldGuilds, WorldRating, WorldReward, WorldRoad, WorldFriends, WorldBattle, WorldBattleMap, WorldBattleAchievements, WorldBattleCamp, WorldClub, WorldClubPage, WorldMatchesHub, WorldBattleDaily, WorldBattleEvent, WorldBattleGuildBoss, WorldBattleItems, WorldBattleProfile, WorldBattleRelics, WorldBattleTeam, WorldEliteRecycle, WorldWeek, WorldStore, WorldLogin, WorldTutorial, WorldTutorialEvent, WorldReminderAnswer } from './worldTypes';
import { useConfig } from './queries';
import { useHasSession } from '../session/favorites';
import { worldPathOf, type PreloadTarget } from '../screens/world/worldModel';
import { hideHint, hintHidden } from '../screens/world/tutorial/hints';

const WORLD_KEY = ['game-world'];
const RATING_KEY = ['game-world-rating'];
const BATTLE_MAP_KEY = ['game-world-battle-map'];
const MATCHES_HUB_KEY = ['game-world-matches-hub'];
const CLUB_KEY = ['game-world-club'];
const WEEK_KEY = ['game-world-week'];
const STORE_KEY = ['game-world-store'];
const ACHIEVEMENTS_KEY = ['game-world-battle-achievements'];

function tzHeaders(): Record<string, string> {
  const tz = deviceTimeZone();
  return tz ? { 'X-Timezone': tz } : {};
}

/** Экран мира; path — адрес сервера как есть (config.game.world_path / endpoint раздела player).
 *  enabled: false — не спрашивать (карточка матча без сессии не заводит гостя ради буста). */
export function useWorld(path: string, enabled = true) {
  return useQuery({
    queryKey: [...WORLD_KEY, path],
    queryFn: ({ signal }) => apiRequest<GameWorld>('GET', path, { auth: true, signal, headers: tzHeaders() }),
    enabled: enabled && Boolean(path),
  });
}

/** Действие мира: относительный путь от адреса мира («chests/<id>/open/», «tasks/<key>/claim/»…). */
export type WorldAction =
  /** «Моя команда» (§2.18; битва фанатов снята §306): тот же POST world/player/; null снимает. */
  | { kind: 'team'; team_id: number | null }
  /** §291: страна-фракция — тот же POST world/player/ {country_id}. */
  | { kind: 'country'; country_id: number }
  | { kind: 'chest'; id: string | number }
  | { kind: 'task'; key: string }
  /** §298: набор за очки заданий — POST по адресу сервера (tasks.points.sets[].endpoint), ответ — экран мира. */
  | { kind: 'task_set'; endpoint: string }
  /** §304: сменить задания окна за самоцветы — POST tasks.reset.endpoint без тела, ответ — экран мира. */
  | { kind: 'tasks_reset'; endpoint: string };
// §278 (выкат 155): награда дня, тренировка, прокачка навыков, вещи, буст ×2, покупка энергии сняты сервером — их не зовём

/** Адрес действия: гид даёт ручки относительно game/{sport}/world/ — дописываем к адресу мира (без его query). */
export function worldActionPath(worldPath: string, action: WorldAction): string {
  const [base = ''] = worldPath.split('?');
  const root = base.endsWith('/') ? base : `${base}/`;
  switch (action.kind) {
    case 'team': case 'country': return `${root}player/`;
    case 'chest': return `${root}chests/${encodeURIComponent(String(action.id))}/open/`;
    case 'task': return `${root}tasks/${encodeURIComponent(action.key)}/claim/`;
    case 'task_set': case 'tasks_reset': return action.endpoint;
  }
}

function actionBody(action: WorldAction): unknown {
  if (action.kind === 'team') return { team_id: action.team_id };
  if (action.kind === 'country') return { country_id: action.country_id };
  return undefined;
}

/** Что показать во всплывашке: медали, опыт, энергия, самоцветы. Монет нет (В4, HQ-782): их ключи сервер держит нулём
 *  до be-economy-remove — не читаем; бонус очков рейтинга и карточки сундука сняты с карточками (В2). */
export type RewardShown = WorldReward;

/** Награда ответа: {medals, xp, gems} или строка сундука ({contents}). Пусто — null. */
export function rewardOf(world: GameWorld): RewardShown | null {
  const r = world.reward;
  if (!r) return null;
  const src = r.contents ?? r;
  const shown = { medals: src.medals ?? 0, xp: src.xp ?? 0, energy: src.energy ?? 0, gems: src.gems ?? 0 };
  // §298: награда бывает и одними самоцветами (набор за очки заданий)
  return shown.medals > 0 || shown.xp || shown.energy || shown.gems ? shown : null;
}

/** Ответ действия — авторитетный экран: во ВСЕ кэши мира (адрес мира и раздела player могут совпадать или нет).
 *  HQ-1530: GET мира в полёте (фокус окна, invalidate после дороги/друзей) сперва отменяем — иначе опоздавший ответ
 *  вернёт мир до действия. Отмена синхронна (query.cancel вызывается сразу), её промис не ждём: запись остаётся
 *  синхронной для вызывающих. */
export function applyWorldResponse(queryClient: ReturnType<typeof useQueryClient>, world: GameWorld): void {
  void queryClient.cancelQueries({ queryKey: WORLD_KEY });
  queryClient.setQueriesData({ queryKey: WORLD_KEY }, world);
}

export function useWorldAction(worldPath: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: WorldAction) => apiRequest<GameWorld>('POST', worldActionPath(worldPath, action),
      { auth: true, body: actionBody(action), headers: tzHeaders() }),
    onSuccess: (world, action) => {
      applyWorldResponse(queryClient, world);
      // «моя команда» (§274): день матча — в экране боя, выбор — в «Клубе»
      // §291: команда — это и гильдия-клуб, и страна по умолчанию: гильдии и «Неделя» тоже
      if (action.kind === 'team') for (const key of [['game-world-rating'], CLUB_KEY, BATTLE_KEY, ['game-world-guilds'], WEEK_KEY]) void queryClient.invalidateQueries({ queryKey: key });
      if (action.kind === 'country') for (const key of [WEEK_KEY, CLUB_KEY]) void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e) => {
      // 409/404 — состояние мира устарело (сундук истёк, задание уже забрано): перечитать экран
      if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: WORLD_KEY });
    },
  });
}

/** Отметка открытой статьи или ролика (§281): POST world/content/earn/ {kind, id} на КАЖДЫЙ открытый материал — первые 4
 *  в день дают золото боя, задание «прочитай новость» засчитывается всегда. Ответ лёгкий {outcome, reward{gold}} —
 *  золото в полосе и бою перечитать без спешки. До выката §281 адреса нет (404) — прежняя энергия: world/energy/earn/,
 *  только если мир знает энергию; ответ — целый мир, в кэш. Только когда мир включён и сессия уже есть. Отказы молча:
 *  это фоновая награда, не действие человека. */
export function useEnergyEarn() {
  const path = worldPathOf(useConfig().data);
  const has = useHasSession();
  const queryClient = useQueryClient();
  return (kind: 'news' | 'highlight', id: number | string) => {
    if (!path || !has) return;
    const key = `${kind}:${id}`;
    if (earned.has(key)) return;
    earned.add(key);
    const body = { kind, id: Number(id) };
    void (async () => {
      try {
        await apiRequest<{ outcome: string; reward?: { gold?: string } }>('POST', worldSubPath(path, 'content/earn/'), { auth: true, body, headers: tzHeaders() });
        for (const k of [WORLD_KEY, BATTLE_KEY]) void queryClient.invalidateQueries({ queryKey: k, refetchType: 'none' });
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 404)) return;
        try {
          const world = await queryClient.fetchQuery({
            queryKey: [...WORLD_KEY, path],
            queryFn: ({ signal }) => apiRequest<GameWorld>('GET', path, { auth: true, signal, headers: tzHeaders() }),
            staleTime: Infinity,
          });
          if (!world.energy) return;
          applyWorldResponse(queryClient, await apiRequest<GameWorld>('POST', worldSubPath(path, 'energy/earn/'), { auth: true, body, headers: tzHeaders() }));
        } catch { /* фоновая награда: отказ не показываем */ }
      }
    })();
  };
}
const earned = new Set<string>();

/** Адрес рядом с адресом мира (без его query): content/earn/, energy/earn/. */
export function worldSubPath(worldPath: string, tail: string): string {
  const [base = ''] = worldPath.split('?');
  return `${base.endsWith('/') ? base : `${base}/`}${tail}`;
}

/** «Рейтинг мира» (этап 3а): адрес — endpoint раздела rating как есть. Первое чтение на неделе ставит в группу дивизиона. */
export function useWorldRating(path: string) {
  return useQuery({
    queryKey: [...RATING_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldRating>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

// ---- Гильдии (этап 3б): только чтение. Действия ручных гильдий и «лига → гильдия» сняты (HQ-1046, двери HQ-784/793);
// выбор клуба — useWorldAction (POST world/player/ {team_id}) ----
const GUILDS_KEY = ['game-world-guilds'];

export function useWorldGuilds(path: string) {
  return useQuery({
    queryKey: [...GUILDS_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldGuilds>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

// ---- Трофейная дорога: claim отвечает ЦЕЛЫМ экраном дороги (+reward) — в кэш; мир перечитать (медали, самоцветы) ----
const ROAD_KEY = ['game-world-road'];

export function useWorldRoad(path: string) {
  return useQuery({
    queryKey: [...ROAD_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldRoad>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

export function roadClaimPath(roadPath: string, tier: number): string {
  const [raw = ''] = roadPath.split('?');
  return `${raw.endsWith('/') ? raw : `${raw}/`}${tier}/claim/`;
}

export function useRoadClaim(roadPath: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tier: number) => apiRequest<WorldRoad>('POST', roadClaimPath(roadPath, tier), { auth: true, headers: tzHeaders() }),
    onSuccess: (road) => {
      queryClient.setQueriesData({ queryKey: ROAD_KEY }, road);
      void queryClient.invalidateQueries({ queryKey: WORLD_KEY });
      // §284: ступень даёт золото боя, вещь и кубки — бой и сумку перечитать без спешки
      for (const key of [BATTLE_KEY, ITEMS_KEY]) void queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' });
    },
    onError: (e) => { if (e instanceof ApiError && e.status === 409) void queryClient.invalidateQueries({ queryKey: ROAD_KEY }); },
  });
}

// ---- Друзья: gift/, gifts/claim/, referral/ отвечают ЦЕЛЫМ экраном (+outcome) — в кэш; мир перечитать (энергия) ----
const FRIENDS_KEY = ['game-world-friends'];

export function useWorldFriends(path: string) {
  return useQuery({
    queryKey: [...FRIENDS_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldFriends>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

export type FriendsAction = { kind: 'gift'; ref: string } | { kind: 'claim' } | { kind: 'referral'; code: string };

export function friendsActionPath(friendsPath: string, a: FriendsAction): string {
  const [raw = ''] = friendsPath.split('?');
  const root = raw.endsWith('/') ? raw : `${raw}/`;
  return a.kind === 'gift' ? `${root}gift/` : a.kind === 'claim' ? `${root}gifts/claim/` : `${root}referral/`;
}

export function useFriendsAction(friendsPath: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: FriendsAction) => apiRequest<WorldFriends>('POST', friendsActionPath(friendsPath, a), {
      auth: true, headers: tzHeaders(),
      body: a.kind === 'gift' ? { ref: a.ref } : a.kind === 'referral' ? { code: a.code } : undefined,
    }),
    onSuccess: (friends) => {
      queryClient.setQueriesData({ queryKey: FRIENDS_KEY }, friends);
      void queryClient.invalidateQueries({ queryKey: WORLD_KEY });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: FRIENDS_KEY }); },
  });
}

// ---- Самоцветы: claim/ и refill/ отвечают ЦЕЛЫМ экраном — в кэш; мир перечитать (энергия, hud.gems) ----
const GEMS_KEY = ['game-world-gems'];

export function useWorldGems(path: string) {
  return useQuery({
    queryKey: [...GEMS_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldGems>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

// ---- §299: календарь входа — GET world/login/; забрать день — POST login.endpoint без тела (ответ — тот же экран). ----
const LOGIN_KEY = ['game-world-login'];
export function useWorldLogin(path: string) {
  return useQuery({ queryKey: [...LOGIN_KEY, path], queryFn: ({ signal }) => apiRequest<WorldLogin>('GET', path, { auth: true, signal, headers: tzHeaders() }) });
}
export function useLoginClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (endpoint: string) => apiRequest<WorldLogin>('POST', endpoint, { auth: true, headers: tzHeaders() }),
    onSuccess: (screen) => {
      queryClient.setQueriesData({ queryKey: LOGIN_KEY }, screen);
      // кошелёк и точка кнопки сцены — из мира
      void queryClient.invalidateQueries({ queryKey: WORLD_KEY });
      void queryClient.invalidateQueries({ queryKey: GEMS_KEY, refetchType: 'none' });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: LOGIN_KEY }); },
  });
}

/** День календаря вернули за просмотр (HQ-198): кошелёк и точка кнопки сцены — из мира, карточка дня — в карточках. */
export function useLoginRewardsStale(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: WORLD_KEY });
    void queryClient.invalidateQueries({ queryKey: GEMS_KEY, refetchType: 'none' });
  };
}

export type GemsAction = { kind: 'claim'; key: string } | { kind: 'refill' };

export function gemsActionPath(gemsPath: string, a: GemsAction): string {
  const [raw = ''] = gemsPath.split('?');
  return `${raw.endsWith('/') ? raw : `${raw}/`}${a.kind}/`;
}

export function useGemsAction(gemsPath: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: GemsAction) => apiRequest<WorldGems>('POST', gemsActionPath(gemsPath, a),
      { auth: true, headers: tzHeaders(), body: a.kind === 'claim' ? { key: a.key } : undefined }),
    onSuccess: (gems) => {
      queryClient.setQueriesData({ queryKey: GEMS_KEY }, gems);
      void queryClient.invalidateQueries({ queryKey: WORLD_KEY });
    },
    onError: (e) => { if (e instanceof ApiError && e.status === 409) void queryClient.invalidateQueries({ queryKey: GEMS_KEY }); },
  });
}

// ---- Мир v3 «Разрушитель калиток»: бой (GET world/battle/ — чтение досчитывает прошедшее время, поэтому без фоновых
// перечитываний), отчёт раз в ~5 с, вызов босса, автопереход; окно «Команда» (найм/улучшение отвечают ЦЕЛЫМ окном). ----
const BATTLE_KEY = ['game-world-battle'];
const TEAM_KEY = ['game-world-battle-team'];
const ITEMS_KEY = ['game-world-battle-items'];
const PROFILE_KEY = ['game-world-battle-profile'];
const RELICS_KEY = ['game-world-battle-relics'];

export function useWorldBattle(path: string, enabled = true) {
  return useQuery({
    queryKey: [...BATTLE_KEY, path],
    enabled: enabled && Boolean(path),
    queryFn: ({ signal }) => apiRequest<WorldBattle>('GET', path, { auth: true, signal, headers: tzHeaders() }),
    // каждое чтение — «досчитать бой»: не перечитывать по фокусу окна посреди боя (отчёты идут своим ходом)
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

/** Адрес действия рядом с экраном боя: report/, boss/, auto/. */
export function battleActionPath(battlePath: string, kind: 'report' | 'boss' | 'auto'): string {
  const [raw = ''] = battlePath.split('?');
  return `${raw.endsWith('/') ? raw : `${raw}/`}${kind}/`;
}

export type BattleAction = { kind: 'report'; kills: number; taps: number } | { kind: 'boss' } | { kind: 'auto'; on: boolean }
  /** Этап 3 (§271): навык и «Новый сезон» — по адресу сервера (skill.endpoint, season.endpoint); ответ — экран боя. */
  | { kind: 'skill'; endpoint: string } | { kind: 'season'; endpoint: string }
  /** §287: ставка на серию — POST streak.bet.endpoint без тела; ответ — экран боя. */
  | { kind: 'streak_bet'; endpoint: string };

/** onScreen — ДО записи ответа в кэш: экран боя узнаёт свой ответ (кэш хранит копию — structural sharing, сравнение по
 *  ссылке не работает) и не перерисовывает бой по серверу (12.10: рывки полосы, двойные смерти, враг поверх умирающего). */
export function useBattleAction(battlePath: string, reportPath?: string, onScreen?: (screen: WorldBattle) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: BattleAction) => apiRequest<WorldBattle>('POST',
      a.kind === 'skill' || a.kind === 'season' || a.kind === 'streak_bet' ? a.endpoint : a.kind === 'report' && reportPath ? reportPath : battleActionPath(battlePath, a.kind),
      { auth: true, body: a.kind === 'report' ? { kills: a.kills, taps: a.taps } : a.kind === 'auto' ? { on: a.on } : undefined, headers: tzHeaders() }),
    onSuccess: (screen) => {
      onScreen?.(screen);
      queryClient.setQueriesData({ queryKey: BATTLE_KEY }, screen);
      // золото в полосе сцены (hud.gold) — из ответа мира: перечитать его без спешки
      void queryClient.invalidateQueries({ queryKey: WORLD_KEY, refetchType: 'none' });
      // выпали вещи — сумка устарела (§270)
      if (screen.result?.drops?.length) void queryClient.invalidateQueries({ queryKey: ITEMS_KEY, refetchType: 'none' });
      // новый сезон сбросил команду и золото, дал кубки (§271)
      if (screen.season_started) for (const key of [TEAM_KEY, PROFILE_KEY, RELICS_KEY]) void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/** Награда за рекламу начислена сервером (золото боя): экран боя из ответа — в кэш (нет экрана — перечитать бой);
 *  полоса мира перечитается без спешки. */
export function useAcceptBattle(): (screen?: WorldBattle) => void {
  const queryClient = useQueryClient();
  return (screen) => {
    if (screen?.battle) queryClient.setQueriesData({ queryKey: BATTLE_KEY }, screen);
    else void queryClient.invalidateQueries({ queryKey: BATTLE_KEY });
    void queryClient.invalidateQueries({ queryKey: WORLD_KEY, refetchType: 'none' });
  };
}

export function useBattleTeam(path: string, enabled = true) {
  return useQuery({
    queryKey: [...TEAM_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldBattleTeam>('GET', path, { auth: true, signal, headers: tzHeaders() }),
    enabled: enabled && Boolean(path),
  });
}

/** Найм/улучшение: POST team/captain/ или team/{player_id}/ {steps}; ответ — всё окно. Сила и золото боя меняются —
 *  экран боя перечитать при возврате. */
export function useTeamUpgrade(teamPath: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: { who: 'captain' | number; steps: string }) => {
      const [raw = ''] = teamPath.split('?');
      const base = raw.endsWith('/') ? raw : `${raw}/`;
      return apiRequest<WorldBattleTeam>('POST', `${base}${a.who}/`, { auth: true, body: { steps: a.steps }, headers: tzHeaders() });
    },
    onSuccess: (team) => {
      queryClient.setQueriesData({ queryKey: TEAM_KEY }, team);
      void queryClient.invalidateQueries({ queryKey: BATTLE_KEY });
      void queryClient.invalidateQueries({ queryKey: WORLD_KEY, refetchType: 'none' });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: TEAM_KEY }); },
  });
}

/** §281: ступень редкости — POST rarity_up.endpoint {currency: medals | gems}; ответ — всё окно «Команда» (+ result).
 *  Сила команды меняется — бой и профиль перечитать. */
export function useRarityUp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: { endpoint: string; currency: 'medals' | 'gems' }) =>
      apiRequest<WorldBattleTeam>('POST', a.endpoint, { auth: true, body: { currency: a.currency }, headers: tzHeaders() }),
    onSuccess: (team) => {
      queryClient.setQueriesData({ queryKey: TEAM_KEY }, team);
      for (const key of [BATTLE_KEY, PROFILE_KEY]) void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: WORLD_KEY, refetchType: 'none' });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: TEAM_KEY }); },
  });
}

/** §296: сжечь комплект в звезду — POST next_star.endpoint без тела; ответ — окно «Команда». Сумка и бой устарели. */
export function useStarUp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (endpoint: string) => apiRequest<WorldBattleTeam>('POST', endpoint, { auth: true, headers: tzHeaders() }),
    onSuccess: (team) => {
      queryClient.setQueriesData({ queryKey: TEAM_KEY }, team);
      for (const key of [BATTLE_KEY, PROFILE_KEY, ITEMS_KEY]) void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: TEAM_KEY }); },
  });
}

export function useBattleMap(path: string) {
  return useQuery({
    queryKey: [...BATTLE_MAP_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldBattleMap>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

export function useBattleProfile(path: string) {
  return useQuery({
    queryKey: [...PROFILE_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldBattleProfile>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

// ---- Этап 2 «Сила игрока команды» (§270): сумка с вещами. Адрес — team.endpoints.items; действия рядом с ним:
// {id}/equip/ {player_id}, {id}/unequip/, merge/ {slot, tier, count?} (count — объединений пачкой, HQ-919 И-12). Ответ действия — вся сумка; сила команды меняется —
// окно «Команда», бой и профиль перечитать. ----
export function useBattleItems(path: string) {
  return useQuery({
    queryKey: [...ITEMS_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldBattleItems>('GET', path, { auth: true, signal, headers: tzHeaders() }),
    enabled: Boolean(path),
  });
}

export type ItemAction = { kind: 'equip'; item_id: number; player_id: number } | { kind: 'unequip'; item_id: number } | { kind: 'merge'; slot: string; tier: number; count?: number };

export function itemActionPath(itemsPath: string, a: ItemAction): string {
  const [raw = ''] = itemsPath.split('?');
  const base = raw.endsWith('/') ? raw : `${raw}/`;
  return a.kind === 'merge' ? `${base}merge/` : `${base}${a.item_id}/${a.kind}/`;
}

/** Тело действия: объединение без count — прежний запрос {slot, tier} (сервер без пачки, HQ-919 И-12). */
export function itemActionBody(a: ItemAction): Record<string, unknown> | undefined {
  if (a.kind === 'equip') return { player_id: a.player_id };
  if (a.kind === 'merge') return a.count === undefined ? { slot: a.slot, tier: a.tier } : { slot: a.slot, tier: a.tier, count: a.count };
  return undefined;
}

export function useItemAction(itemsPath: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: ItemAction) => apiRequest<WorldBattleItems>('POST', itemActionPath(itemsPath, a), {
      auth: true, headers: tzHeaders(), body: itemActionBody(a),
    }),
    onSuccess: (bag) => {
      queryClient.setQueriesData({ queryKey: ITEMS_KEY }, bag);
      for (const key of [TEAM_KEY, BATTLE_KEY, PROFILE_KEY]) void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: ITEMS_KEY }); },
  });
}

// ---- HQ-918 Ш-3: особые герои. Переработка — POST world/battle/elite/recycle/ {hero_id, count}: адрес рядом с окном
// «Команда» (…/battle/team/ → …/battle/elite/recycle/). Ответ — gained и новый список elite; окно «Команда» (мощь,
// строки особых) перечитать, бой и профиль — тоже. 409/404 — перечитать команду. ----
export function eliteRecyclePath(teamPath: string): string | null {
  const [raw = ''] = teamPath.split('?');
  return /\/team\/?$/.test(raw) ? raw.replace(/\/team\/?$/, '/elite/recycle/') : null;
}

export function useEliteRecycle(teamPath: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: { hero_id: string; count: number }) => {
      const path = eliteRecyclePath(teamPath);
      if (!path) return Promise.reject(new ApiError('http', 404, 'elite_hero_not_found'));
      return apiRequest<WorldEliteRecycle>('POST', path, { auth: true, body: { hero_id: a.hero_id, count: a.count }, headers: tzHeaders() });
    },
    onSuccess: (res) => {
      // строки особых — сразу из ответа, остальное окно (мощь) — перечитать
      queryClient.setQueriesData<WorldBattleTeam>({ queryKey: TEAM_KEY }, (team) => (team && res.elite ? { ...team, elite: res.elite } : team));
      for (const key of [TEAM_KEY, BATTLE_KEY, PROFILE_KEY]) void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: TEAM_KEY }); },
  });
}

// ---- Этап 3 (§271): реликвии за кубки. GET world/battle/relics/; поднять — POST relics[].endpoint, ответ — всё окно.
// Сила боя меняется — бой, команду и профиль перечитать. ----
export function useBattleRelics(path: string) {
  return useQuery({
    queryKey: [...RELICS_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldBattleRelics>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

export function useRelicBuy() {
  const queryClient = useQueryClient();
  return useMutation({
    // §294: steps — пачка уровней ("1" | "10" | "25" | "100" | "max"); без него — один уровень (и сброс — тоже без тела)
    mutationFn: (a: { endpoint: string; steps?: string }) => apiRequest<WorldBattleRelics>('POST', a.endpoint, { auth: true, body: a.steps ? { steps: a.steps } : undefined, headers: tzHeaders() }),
    onSuccess: (screen) => {
      queryClient.setQueriesData({ queryKey: RELICS_KEY }, screen);
      for (const key of [BATTLE_KEY, TEAM_KEY, PROFILE_KEY]) void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: RELICS_KEY }); },
  });
}

// ---- Этап 4 (§272): «Капитан дня», лагерь — окна по адресам battle.endpoints.daily / camp.
// Действие — POST по адресу из ответа, ответ — то же окно; бой, команду и сумку перечитать (золото, редкость, вещи). ----
export const META_KEYS = { daily: ['game-world-battle-daily'], camp: ['game-world-battle-camp'],
  event: ['game-world-battle-event'], guildBoss: ['game-world-battle-guild-boss'] } as const;
type MetaKind = keyof typeof META_KEYS;
type MetaScreen = { daily: WorldBattleDaily; camp: WorldBattleCamp; event: WorldBattleEvent; guildBoss: WorldBattleGuildBoss };

export function useBattleMeta<K extends MetaKind>(kind: K, path: string) {
  return useQuery({
    queryKey: [...META_KEYS[kind], path],
    queryFn: ({ signal }) => apiRequest<MetaScreen[K]>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

export function useBattleMetaAction<K extends MetaKind>(kind: K) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: { endpoint: string; body?: Record<string, unknown> }) =>
      apiRequest<MetaScreen[K]>('POST', a.endpoint, { auth: true, body: a.body, headers: tzHeaders() }),
    onSuccess: (screen) => {
      queryClient.setQueriesData({ queryKey: META_KEYS[kind] }, screen);
      for (const key of [BATTLE_KEY, TEAM_KEY, ITEMS_KEY, PROFILE_KEY]) void queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: META_KEYS[kind] }); },
  });
}

/** Мешок добычи просмотрен (§276): POST loot/ — ответ экран боя с пустым loot. Кэш боя НЕ трогаем: смена данных
 *  экрана боя целиком перерисовала бы бой по серверу (прочность — с последнего отчёта). */
export function useLootSeen() {
  return useMutation({ mutationFn: (endpoint: string) => apiRequest<WorldBattle>('POST', endpoint, { auth: true, headers: tzHeaders() }) });
}

/** Сила боя из уже прочитанного экрана боя (для «Капитана дня»: телефон рисует урон между отчётами). */
export function useCachedBattlePower(): { tap: string; dps: string } | null {
  const queryClient = useQueryClient();
  const found = queryClient.getQueriesData<WorldBattle>({ queryKey: BATTLE_KEY }).find(([, d]) => d?.battle?.power);
  return found?.[1]?.battle.power ?? null;
}

// ---- Навигация мира v3 (§273): вкладки «Матчи» (world/matches/) и «Клуб» (world/club/). Блоки «Матчей» — те же, что в
// ответе мира (действия отвечают миром — живые там); «Клуб» после действий гильдии и лиг перечитываем. ----
export function useWorldMatchesHub(path: string) {
  return useQuery({
    queryKey: [...MATCHES_HUB_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldMatchesHub>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

export function useWorldClub(path: string) {
  return useQuery({
    queryKey: [...CLUB_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldClub>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

/** HQ-2341: страница клуба по адресу строки рейтинга (world/clubs/{guild_id}/) — только чтение. 404 не повторяем. */
export function useWorldClubPage(path: string) {
  return useQuery({
    queryKey: ['world-club-page', path],
    queryFn: ({ signal }) => apiRequest<WorldClubPage>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

/** «Неделя» (§285): только чтение; действия — ручками события и босса, после них экран перечитывают. */
export function useWorldWeek(path: string) {
  return useQuery({
    queryKey: [...WEEK_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldWeek>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

/** Витрина (§289). Покупки за самоцветы отвечают витриной — в кэш; кошелёк сцены и бой перечитать. */
export function useWorldStore(path: string) {
  return useQuery({
    queryKey: [...STORE_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldStore>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

export function useStoreBuy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (endpoint: string) => apiRequest<WorldStore>('POST', endpoint, { auth: true, headers: tzHeaders() }),
    onSuccess: (screen) => {
      queryClient.setQueriesData({ queryKey: STORE_KEY }, screen);
      // самоцветы ушли, вещи/буст пришли: кошелёк сцены, бой, сумка, команда — перечитать при открытии
      for (const key of [WORLD_KEY, BATTLE_KEY, ITEMS_KEY, TEAM_KEY]) void queryClient.invalidateQueries({ queryKey: key, refetchType: key === WORLD_KEY ? 'active' : 'none' });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: STORE_KEY }); },
  });
}

/** Достижения боя (§273-B): только чтение. */
export function useBattleAchievements(path: string) {
  return useQuery({
    queryKey: [...ACHIEVEMENTS_KEY, path],
    queryFn: ({ signal }) => apiRequest<WorldBattleAchievements>('GET', path, { auth: true, signal, headers: tzHeaders() }),
  });
}

/** §277: забрать веху самоцветов — POST gems.claim {key}; ответ — то же окно (в кэш), самоцветы шапки и магазина — перечитать. */
export function useAchievementClaim(path: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: { endpoint: string; key: string }) =>
      apiRequest<WorldBattleAchievements>('POST', a.endpoint, { auth: true, body: { key: a.key }, headers: tzHeaders() }),
    onSuccess: (screen) => {
      queryClient.setQueryData(['game-world-battle-achievements', path], screen);
      void queryClient.invalidateQueries({ queryKey: WORLD_KEY, refetchType: 'none' });
    },
    onError: (e) => { if (e instanceof ApiError && (e.status === 409 || e.status === 404)) void queryClient.invalidateQueries({ queryKey: ['game-world-battle-achievements', path] }); },
  });
}

// ---- Предзагрузка (слово владельца 12.10: «экраны, куда можно перейти с главной, — грузить под заставкой»): экран
// кладём в кэш под ТЕМ ЖЕ ключом, что у его хука, — открытый потом экран рисуется сразу (и тихо перечитывается). ----
export type PreloadKind = PreloadTarget['kind'];
export const PRELOAD_KEYS: Record<Exclude<PreloadKind, 'fixtures'>, readonly string[]> = {
  login: LOGIN_KEY, gems: GEMS_KEY, friends: FRIENDS_KEY, road: ROAD_KEY,
  team: TEAM_KEY, battlemap: BATTLE_MAP_KEY, battleprofile: PROFILE_KEY, battleitems: ITEMS_KEY,
  battlerelics: RELICS_KEY, battledaily: META_KEYS.daily, battlecamp: META_KEYS.camp,
  battleachievements: ACHIEVEMENTS_KEY, battleevent: META_KEYS.event, battleguildboss: META_KEYS.guildBoss, guilds: GUILDS_KEY,
  battle: BATTLE_KEY, matches: MATCHES_HUB_KEY, club: CLUB_KEY, rating: RATING_KEY, week: WEEK_KEY, store: STORE_KEY,
};

/** Ключ кэша экрана под предзагрузкой. «Прогнозы» (fixtures/) — ключ useGameFixtures: с признаком сессии (она уже есть). */
export const preloadKey = (kind: PreloadKind, path: string): unknown[] =>
  kind === 'fixtures' ? ['game-fixtures', path, true] : [...PRELOAD_KEYS[kind], path];

/** GET экрана в кэш, если его там нет (свежий не перечитываем); ответ — что лежит в кэше. Ошибка — не беда: экран
 *  спросит сам при открытии. */
export async function preloadWorldScreen(queryClient: QueryClient, kind: PreloadKind, path: string): Promise<unknown> {
  const queryKey = preloadKey(kind, path);
  await queryClient.prefetchQuery({
    queryKey,
    queryFn: ({ signal }) => apiRequest<unknown>('GET', path, { auth: true, signal, headers: tzHeaders() }),
    // бой: каждое чтение досчитывает время — как у useWorldBattle, не перечитывать
    ...(kind === 'battle' ? { staleTime: Infinity } : {}),
  });
  return queryClient.getQueryData(queryKey);
}

// ---- Обучение мира (HQ-190, бэкенд HQ-199). События — POST tutorial.endpoints.events {events: […]} (1–20, по порядку,
// всё или ничего); ответ — экран мира с новым tutorial: во все кэши мира, как ответ действия. Очередь, повтор при сбое
// сети и 409 — в useTutorial (экран о запросах не знает). ----

/** Отправить события обучения; ответ — в кэш мира. Золото полосы сменилось (первый тап даёт стартовое) — бой перечитать. */
export function useTutorialEvents(): (endpoint: string, events: WorldTutorialEvent[]) => Promise<GameWorld> {
  const queryClient = useQueryClient();
  return useCallback(async (endpoint: string, events: WorldTutorialEvent[]) => {
    const world = await apiRequest<GameWorld>('POST', endpoint, { auth: true, body: { events }, headers: tzHeaders() });
    const before = queryClient.getQueriesData<GameWorld>({ queryKey: WORLD_KEY })[0]?.[1]?.scene?.hud.gold;
    applyWorldResponse(queryClient, world);
    if (world.scene && world.scene.hud.gold !== before) void queryClient.invalidateQueries({ queryKey: BATTLE_KEY });
    return world;
  }, [queryClient]);
}

/** 409 tutorial_step_out_of_order и прочие отказы: состояние обучения — только сервера, мир перечитать. */
export function useWorldRefresh(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => { void queryClient.invalidateQueries({ queryKey: WORLD_KEY }); }, [queryClient]);
}

/** «Как играть»: POST how_to_play.endpoint без тела → экран мира с tutorial в форме показа (mode replay). В кэш мира его
 *  НЕ кладём: повтор ничего не меняет, а живая форма tutorial (подсказки) должна остаться; берём только tutorial. */
export function useTutorialReplay() {
  return useMutation({
    mutationFn: async (endpoint: string): Promise<WorldTutorial | null> =>
      (await apiRequest<GameWorld>('POST', endpoint, { auth: true, headers: tzHeaders() })).tutorial ?? null,
  });
}

// ---- Рука-подсказка (HQ-954, И-4): hint {key} у кнопки сцены / вкладки папки → тап шлёт POST world/hint/<key>/seen/. ----

/** Отметить подсказку нажатой: рука прячется сразу (hideHint), запрос — один на ключ; ответ не нужен — мир перечитаем
 *  без спешки (следующее чтение придёт уже без hint). Сбой молча: подсказка — не действие человека, повторит сервер. */
export function useHintSeen(): (key: string) => void {
  const path = worldPathOf(useConfig().data);
  const queryClient = useQueryClient();
  return useCallback((key: string) => {
    if (hintHidden(key)) return;
    hideHint(key);
    if (!path) return;
    void apiRequest<unknown>('POST', worldSubPath(path, `hint/${encodeURIComponent(key)}/seen/`), { auth: true, headers: tzHeaders() })
      .then(() => queryClient.invalidateQueries({ queryKey: WORLD_KEY, refetchType: 'none' }), () => {});
  }, [path, queryClient]);
}

/** HQ-978: вид покровителя (имя, картинка, анимация покоя) по ключу — из уже загруженных витрины (раздел patrons) и
 *  «Команды» (строки source patron); своего запроса нет. Значок таблицы события берёт картинку отсюда, нет — заглушку. */
export interface PatronLook { name: string; art_url: string | null; idle_url: string | null }
export function cachedPatronLooks(qc: QueryClient): Map<string, PatronLook> {
  const looks = new Map<string, PatronLook>();
  for (const [, team] of qc.getQueriesData<WorldBattleTeam>({ queryKey: TEAM_KEY })) {
    for (const h of team?.heroes ?? []) {
      if (h.source === 'patron' && h.patron_key) looks.set(h.patron_key, { name: h.name, art_url: h.art_url || null, idle_url: h.idle_url || null });
    }
  }
  for (const [, store] of qc.getQueriesData<WorldStore>({ queryKey: STORE_KEY })) {
    for (const h of (store?.patrons?.sets ?? []).flatMap((s) => s.heroes)) {
      if (!looks.has(h.key)) looks.set(h.key, { name: h.name, art_url: h.art_url || null, idle_url: h.idle_url || null });
    }
  }
  return looks;
}

// ---- Напоминания (HQ-958, И-15a): ответ на окно «Turn on reminders?» ----

/** Адрес ответа: <sport>/world/reminders/answer/ рядом с адресом мира. */
export const reminderAnswerPath = (worldPath: string) => worldSubPath(worldPath, 'reminders/answer/');

/** Ответ на окно: POST {answer} → {outcome:"saved"}. Окно снимаем сразу во всех кэшах мира (повтор решает сервер:
 *  «later» — снова через 7 дней). Сбой не показываем: не дошло — сервер спросит на следующем заходе. */
export function useReminderAnswer(worldPath: string): (answer: WorldReminderAnswer) => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async (answer: WorldReminderAnswer) => {
    queryClient.setQueriesData<GameWorld>({ queryKey: WORLD_KEY }, (w) => (w?.reminder_prompt ? { ...w, reminder_prompt: null } : w));
    try {
      await apiRequest<{ outcome: string }>('POST', reminderAnswerPath(worldPath), { auth: true, body: { answer }, headers: tzHeaders() });
    } catch { /* фоновая отметка: отказ не показываем */ }
  }, [queryClient, worldPath]);
}
